import React from 'react';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Link from '@amzn/awsui-components-console/link';
import Badge from '@amzn/awsui-components-console/badge';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';

interface MetaBoxProps {
  cxFamilyId: string;
  cxIds: string[];
  updatedAt?: number | null;
  updatedBy?: string | null;
  /** When set, renders variant-specific metadata */
  variantCxId?: string;
  /** URL to navigate back to the base string (variant view only) */
  baseStringHref?: string;
  /** String group version from the API (shown in variant view only) */
  stringGroupVersion?: string | null;

  // TODO: DescribeString does not return string-level createdOn/createdBy today.
  // These would need to come from a DescribeStringGroup call (group-level fields)
  // or a schema addition. Sourced from mock data for now for UI parity with the
  // design mockup.
  /** Created-on date as epoch seconds (mock/demo-only pending backend support) */
  createdOn?: number | null;
  /** Alias of the creator (mock/demo-only pending backend support) */
  createdBy?: string | null;

  /** String group ID for the "Additional Information" section (real, from URL params) */
  stringGroupId?: string;

  // TODO: The following "Additional Information" fields have no backing API field
  // in DescribeString or DescribeStringGroup today. They are included purely for
  // mockup-visual-parity in mock/demo mode. Wire them to real API fields when/if
  // the backend is extended to support them.
  /** Namespace owner (mock/demo-only — no API field today) */
  owner?: string | null;
  /** Bindle permissions link text (mock/demo-only — no API field today) */
  permissions?: string | null;
  /** PSMS identifier (mock/demo-only — no API field today) */
  psmsId?: string | null;
  /** Anchor identifier (mock/demo-only — no API field today) */
  anchor?: string | null;
}

/**
 * Formats an epoch-seconds timestamp as DD/MM/YYYY to match the design mockup.
 * WARNING: passing a milliseconds value here would produce an incorrect far-future date.
 */
const formatEpochAsDate = (epochSeconds: number): string => {
  const date = new Date(epochSeconds * 1000);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

/** Renders a single key-value row with label left-aligned and value right-aligned.
 *  Uses minWidth:0 on flex children to allow shrinking below content size,
 *  and overflowWrap/wordBreak on the value to wrap long unbreakable tokens
 *  (e.g. "PSMS-ID-696cbab1-f860") instead of forcing horizontal overflow. */
const MetaRow: React.FC<{
  label: string;
  children: React.ReactNode;
  'data-testid'?: string;
}> = ({ label, children, 'data-testid': testId }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: '8px',
    }}
  >
    <div style={{ flexShrink: 0 }}>
      <Box variant="awsui-key-label" fontSize="body-s">
        {label}
      </Box>
    </div>
    <div
      style={{
        minWidth: 0,
        overflowWrap: 'anywhere',
        wordBreak: 'break-all',
        textAlign: 'right',
      }}
      data-testid={testId}
    >
      <Box fontSize="body-s">{children}</Box>
    </div>
  </div>
);

const MetaBox: React.FC<MetaBoxProps> = ({
  cxFamilyId,
  cxIds,
  updatedAt,
  updatedBy,
  variantCxId,
  baseStringHref,
  stringGroupVersion,
  createdOn,
  createdBy,
  stringGroupId,
  owner,
  permissions,
  psmsId,
  anchor,
}) => {
  const isVariant = !!variantCxId;
  const hasAdditionalInfo = !!(
    stringGroupId ||
    owner ||
    permissions ||
    psmsId ||
    anchor
  );

  return (
    <Container header={<Header variant="h3">Details</Header>}>
      <SpaceBetween size="xxs">
        {isVariant && (
          <>
            <MetaRow label="Type" data-testid="meta-type-badge">
              <Badge color="blue">Variant</Badge>
            </MetaRow>
            <MetaRow label="CX ID" data-testid="meta-cxid">
              {variantCxId}
            </MetaRow>
            {baseStringHref && (
              <MetaRow label="Base String" data-testid="meta-base-string-link">
                <Link href={baseStringHref}>View base string</Link>
              </MetaRow>
            )}
            {/* Group Version: shown in variant view only. "· Latest" suffix from
                the mockup is intentionally omitted — DescribeString does not
                indicate whether the returned version is the latest without an
                additional ListStringGroupVersions call. */}
            {stringGroupVersion != null && (
              <MetaRow label="Group Version" data-testid="meta-group-version">
                v{stringGroupVersion}
              </MetaRow>
            )}
          </>
        )}
        <MetaRow label="CX Family" data-testid="meta-cx-family">
          {cxFamilyId}
        </MetaRow>
        <MetaRow label="CX IDs" data-testid="meta-cx-ids">
          {cxIds.length > 0 ? cxIds.join(', ') : '—'}
        </MetaRow>
        {createdOn != null && (
          <MetaRow label="Created On" data-testid="meta-created-on">
            {formatEpochAsDate(createdOn)}
          </MetaRow>
        )}
        {createdBy && (
          <MetaRow label="Created By" data-testid="meta-created-by">
            {createdBy}
          </MetaRow>
        )}
        {updatedAt != null && (
          <MetaRow label="Last Edited On" data-testid="meta-last-edited-on">
            {formatEpochAsDate(updatedAt)}
          </MetaRow>
        )}
        {updatedBy && (
          <MetaRow label="Last Edited By" data-testid="meta-last-edited-by">
            {updatedBy}
          </MetaRow>
        )}
        {hasAdditionalInfo && (
          <ExpandableSection
            headerText="Additional Information"
            data-testid="additional-info-summary"
          >
            <div data-testid="additional-info-details">
              <SpaceBetween size="xxs">
                {owner && (
                  <MetaRow label="Owner" data-testid="meta-owner">
                    {owner}
                  </MetaRow>
                )}
                {stringGroupId && (
                  <MetaRow label="Group" data-testid="meta-group">
                    {stringGroupId}
                  </MetaRow>
                )}
                {permissions && (
                  <MetaRow label="Permissions" data-testid="meta-permissions">
                    {permissions}
                  </MetaRow>
                )}
                {!isVariant && psmsId && (
                  <MetaRow label="PSMS ID" data-testid="meta-psms-id">
                    {psmsId}
                  </MetaRow>
                )}
                {!isVariant && anchor && (
                  <MetaRow label="Anchor" data-testid="meta-anchor">
                    {anchor}
                  </MetaRow>
                )}
              </SpaceBetween>
            </div>
          </ExpandableSection>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default MetaBox;
